import { api, apiBlob } from '@/lib/api';

export interface CheatsheetArtifact {
  paper: 'A4' | 'A3';
  colour: 'colour' | 'mono';
  orientation: 'portrait' | 'landscape';
  filename: string;
  pages: number;
  bytes: number;
  sha256: string;
}
export interface OfficialCheatsheet {
  html?: { filename: string; bytes: number; sha256: string };
  development_review?: boolean;
  html_review?: boolean;
  fixed_html?: boolean;
  html_papers?: Array<'A4' | 'A3'>;
  key: string;
  version: string;
  year: number;
  subject: string;
  term?: number;
  topic: string;
  title: string;
  kind?: 'term-overview' | 'topic-detail';
  term_scope?: { status: 'unconfirmed' | 'confirmed'; label: string; term_number?: number | null };
  diagrams?: Array<{ id: string; alt: string; entry_ids: string[]; svg_base64: string }>;
  sections?: Array<{ id: string; title: string; entry_ids: string[]; category?: string }>;
  intro: string;
  scope: string;
  prerequisites: string;
  checklist: string;
  challenge: string;
  challenge_answer: string;
  entries: Array<{
    id: string;
    title: string;
    formula: string;
    latex: string;
    symbols: string;
    conditions?: string;
    steps?: string;
    blocks?: Array<{ kind: string; value: string }>;
    use_when: string;
    example: string;
    mistake: string;
    diagram: string;
  }>;
  artifacts: CheatsheetArtifact[];
}
export const listOfficialCheatsheets = () =>
  api<{ sheets: OfficialCheatsheet[] }>('/study/cheatsheets');
export const cheatsheetPdfPath = (
  sheet: Pick<OfficialCheatsheet, 'key' | 'version'>,
  artifact: CheatsheetArtifact,
) =>
  `/study/cheatsheets/${encodeURIComponent(sheet.key)}/${encodeURIComponent(sheet.version)}/pdf?paper=${artifact.paper}&colour=${artifact.colour}`;

export async function verifiedCheatsheetPdf(
  sheet: OfficialCheatsheet,
  artifact: CheatsheetArtifact,
) {
  const blob = sheet.development_review
    ? await api<{ pdf_base64: string }>(
        `/__study-cheatsheet-review/pdf?key=${encodeURIComponent(sheet.key)}&version=${encodeURIComponent(sheet.version)}&paper=${artifact.paper}&colour=${artifact.colour}`,
        { localStudyReview: true },
      ).then(
        ({ pdf_base64 }) =>
          new Blob([Uint8Array.from(atob(pdf_base64), (character) => character.charCodeAt(0))], {
            type: 'application/pdf',
          }),
      )
    : await apiBlob(cheatsheetPdfPath(sheet, artifact));
  const bytes = await blob.arrayBuffer();
  const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
  if (
    bytes.byteLength !== artifact.bytes ||
    hash !== artifact.sha256 ||
    new TextDecoder().decode(bytes.slice(0, 5)) !== '%PDF-'
  )
    throw new Error('This PDF could not be verified. Reload the catalogue and try again.');
  return blob;
}
export function saveCheatsheetPdf(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const reviewEnabled =
  import.meta.env.DEV && import.meta.env.VITE_STUDY_CHEATSHEET_REVIEW === 'true';
export const listReviewCheatsheets = () =>
  api<{ sheets: OfficialCheatsheet[] }>('/__study-cheatsheet-review/catalogue', {
    localStudyReview: true,
  });
export const readReviewHtml = (sheet: OfficialCheatsheet) =>
  api<{ html: string; sha256: string }>(
    sheet.development_review
      ? `/__study-cheatsheet-review/html?key=${encodeURIComponent(sheet.key)}&version=${encodeURIComponent(sheet.version)}`
      : `/study/cheatsheets/${encodeURIComponent(sheet.key)}/${encodeURIComponent(sheet.version)}/html`,
    sheet.development_review ? { localStudyReview: true } : {},
  );
